import React, { FC, useEffect } from 'react'
import Box from '@mui/material/Box'
import { Popover } from '@mui/material'

import SidebarAlert from './SidebarAlert'
import SidebarAlertsHead from './SidebarAlertsHead'
import { AppIcon } from '../../../../../../components/AppIcon'
import { SidebarNotificationBadge } from '../SidebarNotificationBadge'
import { ALERTS_UPDATE_PERIOD } from '../../../../../../constants/alerts'
import SidebarAlertsReducer from './AlertsForSidebar/SidebarAlertsReducer'
import { useTypedSelector } from '../../../../../../hooks/useTypedSelector'
import { SidebarAlertsBadgeLoader, SidebarAlertsOpenButton } from './styled'
import ClosePopoversProvider from '../../../../../../components/ClosePopoversProvider'
import { useDefaultComponentTools } from '../../../../../../hooks/useDefaultComponentTools'
import { getRealtimeAlerts } from '../../../../../../store/reducers/realtimeAlerts/actions'
import { selectSortedByTimeRealtimeAlerts } from '../../../../../../store/reducers/realtimeAlerts/selectors'

const alertsPopoverSx = {
	ml: 4,
	mb: 13,
	'& .MuiPaper-root': {
		overflow: 'hidden',
		display: 'flex',
		flexDirection: 'column',
		width: '26rem',
		borderRadius: '1rem',
	},
}

const SidebarAlerts: FC = () => {
	const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
		null
	)
	const { loading, setLoading, alert, isMounted, dispatch } =
		useDefaultComponentTools()

	const realtimeAlerts = useTypedSelector(selectSortedByTimeRealtimeAlerts)

	useEffect(() => {
		let timerId = 0 as unknown as ReturnType<typeof setTimeout>

		const fetchRealtimeAlerts = async (): Promise<void> => {
			try {
				setLoading(true)

				await dispatch(getRealtimeAlerts())

				timerId = setTimeout(
					() => fetchRealtimeAlerts(),
					ALERTS_UPDATE_PERIOD
				)
			} catch (e: any) {
				'message' in e && e.message && alert.error(e.message)

				clearTimeout(timerId)
			} finally {
				isMounted() && setLoading(false)
			}
		}

		fetchRealtimeAlerts()

		return () => {
			clearTimeout(timerId)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch])

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
		setAnchorEl(event.currentTarget)
	}

	const handleClose = (): void => {
		setAnchorEl(null)
	}

	const open = Boolean(anchorEl)
	const id = open ? 'alerts-popover' : undefined

	return (
		<>
			<SidebarAlertsOpenButton
				aria-label='open alerts popup'
				onClick={handleClick}
				aria-describedby={id}
				open={open}
			>
				<SidebarNotificationBadge
					badgeContent={!loading ? realtimeAlerts.length : null}
					color='orange'
				>
					<AppIcon type={'bell'} />
				</SidebarNotificationBadge>

				{loading && (
					<Box>
						<SidebarAlertsBadgeLoader
							color={'orange'}
							size={23}
							thickness={7}
						/>
					</Box>
				)}
			</SidebarAlertsOpenButton>

			<Popover
				id={id}
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: 'center',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'center',
					horizontal: 'left',
				}}
				marginThreshold={34}
				sx={alertsPopoverSx}
			>
				<ClosePopoversProvider onClose={handleClose}>
					<SidebarAlertsHead />

					<Box py={4} px={3} flexGrow={1} overflow={'auto'}>
						{realtimeAlerts.map(alert => (
							<SidebarAlert key={alert._id} alert={alert}>
								<SidebarAlertsReducer alert={alert} />
							</SidebarAlert>
						))}
					</Box>
				</ClosePopoversProvider>
			</Popover>
		</>
	)
}

export default SidebarAlerts
